import { Component, OnInit, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { ClientService } from 'src/app/services/user/client.service';
import { DepositModalService } from 'src/app/services/deposit-modal/deposit-modal.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DepositService } from 'src/app/services/deposit/deposit.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-update-deposit',
  templateUrl: './add-update-deposit.component.html',
  styleUrls: ['./add-update-deposit.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddUpdateDepositComponent extends DestructorComponent implements OnInit {

  @Output() depositAdded = new EventEmitter<void>();
  @Output() depositUpdated = new EventEmitter<void>();

  depositForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedDeposit: any = null;
  currencyType:string = "jpy";

  // Dropdown data
  banks: any[] = [];
  paymentTypes: any[] = [];

  // Customer search properties
  customerSearchTerm: string = '';
  customerSearchResults: any[] = [];
  isCustomerSearching: boolean = false;
  showCustomerDropdown: boolean = false;
  selectedCustomer: any = null;
  private customerSearchSubject = new Subject<string>();

  // File uploads
  incomingTTCopyFile: File | null = null;
  customerTTCopyFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    private _globalStateService: GlobalStateService,
    private _clientService: ClientService,
    private _depositModalService: DepositModalService,
    private _depositService: DepositService
  ) {
    super();
    this.setupCustomerSearchDebounce();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadSettings();
    this.setupModalListener();
  }


  initializeForm(): void {
    this.depositForm = this.fb.group({
      reference_number: [{ value: 'DEP-REF-', disabled: true }],
      payment_type: ['', [Validators.required]],
      customer_id: ['', [Validators.required]],
      customer_name: ['', [Validators.required]],
      agent_name: [{ value: '', disabled: true }],
      bank: ['', [Validators.required]],
      received_date: ['', [Validators.required]],
      received_amount: ['', [Validators.required, Validators.min(0)]],
      depositor_name: [''],
      admin_comment: ['', [Validators.maxLength(250)]],
      refund_amount: ['', [Validators.min(0)]],
      refund_date: [''],
      balance_amount: ['', [Validators.min(0)]],
      r_rate: [''],
      ex_rate: [''],
      chassis_no: [''],
      incoming_tt_copy: [''],
      customer_tt_copy: ['']
    });
  }

  loadSettings(): void {
    this.subscriptions.push(
      this._globalStateService.GetSetting().subscribe(settings => {
        if (settings) {
          this.banks = settings.banks || [];
          this.paymentTypes = settings.payment_types || [];
        }
      })
    );
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
        this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;

    // Update the form control with the typed value
    this.depositForm.patchValue({
      customer_name: this.customerSearchTerm
    });
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;

  }

  private searchCustomers(searchTerm: string): void {
    this.isCustomerSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    const customerName = this.getCustomerDisplayName(customer);
    this.customerSearchTerm = customerName;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.currencyType = customer.currency_type || "";

    // Update the form with customer data
    this.depositForm.patchValue({
      customer_id: customer._id || customer.id || '',
      customer_name: customerName,
      agent_name: customer.sales_person || ''
    });
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];

    // Reset customer fields
    this.depositForm.patchValue({
      customer_id: '',
      customer_name: '',
      agent_name: ''
    });
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}, ${customer.currency_type}`;
    }
    return customer.name || customer.company || '';
  }

  onIncomingTTCopyChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.incomingTTCopyFile = file;
      this.depositForm.patchValue({ incoming_tt_copy: file.name });
    }
  }

  onCustomerTTCopyChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.customerTTCopyFile = file;
      this.depositForm.patchValue({ customer_tt_copy: file.name });
    }
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceDeposit = this._depositModalService.getSelectedDeposit();
        if (serviceDeposit) {
          this.isEditMode = true;
          this.selectedDeposit = serviceDeposit;
          this.populateForm(this.selectedDeposit);
        } else {
          this.isEditMode = false;
          this.selectedDeposit = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._depositModalService.clearSelectedDeposit();
        this.resetForm();
      });
    }
  }

  populateForm(deposit: any): void {
    this.depositForm.patchValue({
      reference_number: deposit.reference_number || 'DEP-REF-',
      payment_type: deposit.payment_type || '',
      customer_id: deposit.customer_id || '',
      customer_name: deposit.customer.name || '',
      agent_name: deposit.customer.sales_person || '',
      bank: deposit.bank || '',
      received_date: deposit.received_date || '',
      received_amount: deposit.received_amount || '',
      depositor_name: deposit.depositor_name || '',
      admin_comment: deposit.admin_comment || '',
      refund_amount: deposit.refund_amount || '',
      refund_date: deposit.refund_date || '',
      balance_amount: deposit.balance_amount || '',
      r_rate: deposit.r_rate || '',
      ex_rate: deposit.ex_rate || '',
      chassis_no: deposit.chassis_no || '',
      incoming_tt_copy: deposit.incoming_tt_copy || '',
      customer_tt_copy: deposit.customer_tt_copy || ''
    });

    this.selectedCustomer = deposit.customer;
    this.customerSearchTerm = this.getCustomerDisplayName(deposit.customer);
    console.log('Currency type:', deposit.customer.currency_type);
    this.currencyType = deposit.customer.currency_type || "";
  }

  onSubmit(): void {
    if (this.depositForm.invalid) {
      this.markFormGroupTouched(this.depositForm);
      return;
    }

    this.isLoading = true;

    // Prepare JSON data according to backend validation schema
    const formValues = this.depositForm.getRawValue();
    const jsonData: any = {
      customer_id: formValues.customer_id,
      payment_type: formValues.payment_type,
      bank: formValues.bank,
      received_amount: formValues.received_amount,
      received_date: formValues.received_date
    };

    // Add optional fields only if they have values
    if (formValues.admin_comment) {
      jsonData.admin_comment = formValues.admin_comment;
    }
    if (formValues.chassis_no) {
      jsonData.chassis_no = formValues.chassis_no;
    }
    if (formValues.depositor_name) {
      jsonData.depositor_name = formValues.depositor_name;
    }
    if (formValues.r_rate) {
      jsonData.r_rate = formValues.r_rate;
    }
    if (formValues.ex_rate) {
      jsonData.ex_rate = formValues.ex_rate;
    }
    if (formValues.refund_amount) {
      jsonData.refund_amount = formValues.refund_amount;
    }
    if (formValues.refund_date) {
      jsonData.refund_date = formValues.refund_date;
    }

    if(formValues.balance_amount){
      jsonData.balance_amount = formValues.balance_amount;
    }

    // Call API - use UpdateData if in edit mode, otherwise AddData
    const apiCall = this.isEditMode
      ? this._depositService.UpdateData(this.selectedDeposit._id, jsonData)
      : this._depositService.AddData(jsonData);

    this.subscriptions.push(
      apiCall.subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            // Show success message
            Swal.fire({
              text: `Deposit has been successfully ${this.isEditMode ? 'updated' : 'added'}!`,
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();
                if (this.isEditMode) {
                  this.depositUpdated.emit();
                } else {
                  this.depositAdded.emit();
                }
                this.resetForm();
              }
            });
          } else {
            // Handle error response
            Swal.fire({
              text: response.message || "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error(`Error ${this.isEditMode ? 'updating' : 'adding'} deposit:`, error);

          // Show error message
          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  resetForm(): void {
    this.depositForm.reset({
      reference_number: 'DEP-REF-',
      payment_type: '',
      customer_id: '',
      customer_name: '',
      agent_name: '',
      bank: '',
      received_date: '',
      received_amount: '',
      depositor_name: '',
      admin_comment: '',
      refund_amount: '',
      refund_date: '',
      balance_amount: '',
      r_rate: '',
      ex_rate: '',
      chassis_no: '',
      incoming_tt_copy: '',
      customer_tt_copy: ''
    });
    this.incomingTTCopyFile = null;
    this.customerTTCopyFile = null;
    this.isEditMode = false;
    this.selectedDeposit = null;
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.customerSearchResults = [];
    this.showCustomerDropdown = false;
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  getAdminCommentLength(): number {
    return this.depositForm.get('admin_comment')?.value?.length || 0;
  }
}
